import React, { useState, useEffect } from 'react';
import { Key, Wifi, Calendar, Sun, Moon, Thermometer, Lock, Unlock, ChevronLeft, ChevronRight, Check } from 'lucide-react';

/* ─── Browser Frame Wrapper ─── */
function BrowserMockup({ urlBar, children, height }) {
  return (
    <div className="rounded-sm overflow-hidden shadow-2xl relative"
      style={{ border: '1px solid rgba(201,168,76,0.25)', height: height || 'auto' }}>
      {/* Chrome bar */}
      <div className="flex items-center gap-2 px-4 py-2.5"
        style={{ background: '#0c0c0c', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex gap-1.5">
          {['#3d3d3d', '#4a4a4a', '#4a4a4a'].map((c, i) => (
            <div key={i} className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />
          ))}
        </div>
        <div className="flex-1 mx-4 px-3 py-1 rounded text-[10px] text-center"
          style={{ background: '#1a1a1a', color: '#444', maxWidth: 300, margin: '0 auto', fontFamily: 'monospace' }}>
          {urlBar}
        </div>
        <div className="w-16" />
      </div>
      {/* Content */}
      <div style={{ background: '#0a0a0a' }}>
        {children}
      </div>
      {/* Gold glow behind */}
      <div className="absolute -inset-6 -z-10 opacity-15 rounded-sm pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, #c9a84c 0%, transparent 70%)' }} />
    </div>
  );
}

/* ─── Animated stat counter ─── */
function Counter({ target, prefix = '', suffix = '' }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let frame;
    const start = performance.now();
    const duration = 1200;
    const animate = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(Math.round(eased * target));
      if (progress < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [target]);
  return <>{prefix}{val.toLocaleString('it-IT')}{suffix}</>;
}

/* ─── KPI Card ─── */
function KpiCard({ label, value, sub, accent }) {
  return (
    <div className="p-3 rounded-sm" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
      <p className="text-[9px] uppercase tracking-widest mb-1" style={{ color: '#555' }}>{label}</p>
      <p className="font-serif text-xl font-light" style={{ color: accent || '#c9a84c' }}>{value}</p>
      {sub && <p className="text-[10px] mt-0.5" style={{ color: '#3a3a3a' }}>{sub}</p>}
    </div>
  );
}

/* ─── Toggle Switch ─── */
function ToggleRow({ label, icon: Icon, on, accent }) {
  const [active, setActive] = useState(on);
  return (
    <div onClick={() => setActive(a => !a)}
      className="flex items-center justify-between px-3 py-2.5 rounded-sm cursor-pointer transition-colors"
      style={{ background: active ? 'rgba(201,168,76,0.06)' : 'rgba(255,255,255,0.02)', border: `1px solid ${active ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.05)'}` }}>
      <div className="flex items-center gap-2.5">
        <div className="w-6 h-6 rounded flex items-center justify-center"
          style={{ background: active ? 'rgba(201,168,76,0.15)' : 'rgba(255,255,255,0.04)' }}>
          <Icon size={12} style={{ color: active ? '#c9a84c' : '#444' }} />
        </div>
        <span className="text-[11px]" style={{ color: active ? '#ccc' : '#555' }}>{label}</span>
      </div>
      <div className="w-7 h-3.5 rounded-full relative" style={{ background: active ? '#c9a84c' : '#2a2a2a' }}>
        <div className="absolute top-0.5 w-2.5 h-2.5 rounded-full bg-white"
          style={{ left: active ? '15px' : '2px', transition: 'left 0.15s ease' }} />
      </div>
    </div>
  );
}

/* ─── Dashboard Mockup ─── */
function DashboardMockup({ lang }) {
  const bookings = [
    { name: 'Marco R.', room: 'Suite A', date: '24 gen', color: '#c9a84c' },
    { name: 'Sarah K.', room: 'Villa', date: '26 gen', color: '#7c3aed' },
    { name: 'Luca B.', room: 'Studio', date: '28 gen', color: '#0891b2' },
  ];
  return (
    <div className="p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div>
          <p className="text-[9px] uppercase tracking-widest" style={{ color: '#c9a84c' }}>Dashboard</p>
          <p className="text-sm font-light" style={{ color: '#ccc' }}>Villa Aurora</p>
        </div>
        <div className="w-6 h-6 rounded-sm flex items-center justify-center" style={{ background: '#c9a84c' }}>
          <Key size={11} color="#000" />
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 gap-2">
        <KpiCard label={lang === 'it' ? 'Prenotazioni oggi' : 'Bookings today'} value={<Counter target={3} />} />
        <KpiCard label={lang === 'it' ? 'Incasso mese' : 'Monthly revenue'} value={<>€<Counter target={2840} /></>} />
        <KpiCard label={lang === 'it' ? 'Occupazione' : 'Occupancy'} value={<><Counter target={87} />%</>} accent="#059669" />
        <KpiCard label={lang === 'it' ? 'Ospiti attivi' : 'Active guests'} value={<Counter target={7} />} accent="#7c3aed" />
      </div>

      {/* Booking list */}
      <div>
        <p className="text-[9px] uppercase tracking-widest mb-2" style={{ color: '#444' }}>
          {lang === 'it' ? 'Prossime prenotazioni' : 'Upcoming bookings'}
        </p>
        <div className="space-y-1.5">
          {bookings.map((b, i) => (
            <div key={i} className="flex items-center gap-2.5 py-2 px-3 rounded-sm"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
              <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: b.color }} />
              <span className="text-[11px] flex-1" style={{ color: '#bbb' }}>{b.name}</span>
              <span className="text-[10px]" style={{ color: '#555' }}>{b.room}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.04)', color: '#666' }}>{b.date}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Quick stats bar */}
      <div className="flex items-center gap-3 pt-1">
        <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: '#1a1a1a' }}>
          <div className="h-full rounded-full" style={{ width: '87%', background: 'linear-gradient(to right, #c9a84c, #a07830)' }} />
        </div>
        <span className="text-[10px]" style={{ color: '#555' }}>87% {lang === 'it' ? 'pieno' : 'full'}</span>
      </div>
    </div>
  );
}

/* ─── Smart Home Mockup ─── */
function SmartHomeMockup({ lang }) {
  return (
    <div className="p-5 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-[9px] uppercase tracking-widest" style={{ color: '#c9a84c' }}>Smart Home</p>
          <p className="text-sm font-light" style={{ color: '#ccc' }}>Suite A</p>
        </div>
        <div className="text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(5,150,105,0.12)', color: '#059669', border: '1px solid rgba(5,150,105,0.25)' }}>
          {lang === 'it' ? '4 dispositivi' : '4 devices'}
        </div>
      </div>

      {/* Device toggles */}
      <div className="space-y-2">
        <ToggleRow label={lang === 'it' ? 'Serratura principale' : 'Main lock'} icon={Lock} on={true} />
        <ToggleRow label={lang === 'it' ? 'Luci soggiorno' : 'Living room lights'} icon={Sun} on={true} />
        <ToggleRow label={lang === 'it' ? 'Clima — 22°C' : 'Climate — 22°C'} icon={Thermometer} on={true} />
        <ToggleRow label={lang === 'it' ? 'Cancello ingresso' : 'Entrance gate'} icon={Unlock} on={false} />
      </div>

      {/* Virtual key card */}
      <div className="mt-3 p-3 rounded-sm" style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.2)' }}>
        <div className="flex items-center gap-2 mb-2">
          <Key size={11} style={{ color: '#c9a84c' }} />
          <p className="text-[10px] uppercase tracking-widest" style={{ color: '#c9a84c' }}>
            {lang === 'it' ? 'Chiave Virtuale attiva' : 'Virtual Key active'}
          </p>
        </div>
        <p className="text-xs" style={{ color: '#888' }}>Marco Rossi</p>
        <div className="flex items-center justify-between mt-1">
          <p className="text-[10px]" style={{ color: '#555' }}>
            {lang === 'it' ? 'Scade: 26 gen 12:00' : 'Expires: Jan 26 12:00'}
          </p>
          <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#059669' }} />
        </div>
      </div>
    </div>
  );
}

/* ─── Booking Portal Mockup ─── */
function BookingPortalMockup({ lang }) {
  const days = [20, 21, 22, 23, 24, 25, 26];
  const booked = [20, 21, 25, 26];
  const selected = [22, 23, 24];
  const dayNames = lang === 'it' ? ['L', 'M', 'M', 'G', 'V', 'S', 'D'] : ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  return (
    <div className="p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <div className="w-8 h-8 rounded-sm flex items-center justify-center" style={{ background: '#c9a84c' }}>
          <Key size={14} color="#000" />
        </div>
        <div>
          <p className="text-sm font-light" style={{ color: '#ccc' }}>Villa Aurora</p>
          <p className="text-[10px]" style={{ color: '#555' }}>
            {lang === 'it' ? 'Portale Prenotazioni' : 'Booking Portal'}
          </p>
        </div>
      </div>

      {/* Month nav */}
      <div className="flex items-center justify-between px-1">
        <button className="p-1 rounded" style={{ color: '#555' }}><ChevronLeft size={13} /></button>
        <p className="text-xs font-medium" style={{ color: '#ccc' }}>
        {lang === 'it' ? 'Gennaio 2026' : 'January 2026'}
        </p>
        <button className="p-1 rounded" style={{ color: '#555' }}><ChevronRight size={13} /></button>
      </div>

      {/* Day names */}
      <div className="grid grid-cols-7 gap-1">
        {dayNames.map((d, i) => (
          <div key={i} className="text-center text-[9px] uppercase tracking-widest" style={{ color: '#444' }}>{d}</div>
        ))}
      </div>

      {/* Calendar days */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((d) => {
          const isBooked = booked.includes(d);
          const isSel = selected.includes(d);
          return (
            <div key={d} className="aspect-square flex items-center justify-center rounded text-[11px] font-medium"
              style={{
                background: isBooked ? 'rgba(220,38,38,0.12)' : isSel ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.03)',
                color: isBooked ? '#555' : isSel ? '#c9a84c' : '#888',
                border: isSel ? '1px solid rgba(201,168,76,0.4)' : '1px solid transparent',
                textDecoration: isBooked ? 'line-through' : 'none',
              }}>
              {d}
            </div>
          );
        })}
      </div>

      {/* Selection summary */}
      <div className="p-3 rounded-sm" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)' }}>
        <div className="flex justify-between items-center mb-2">
          <p className="text-[10px]" style={{ color: '#888' }}>
            {lang === 'it' ? '22–24 gennaio · 3 notti' : 'Jan 22–24 · 3 nights'}
          </p>
          <p className="text-sm font-semibold" style={{ color: '#c9a84c' }}>€540</p>
        </div>
        <div className="flex items-center justify-center gap-2 py-2 rounded-sm text-xs font-semibold"
          style={{ background: '#c9a84c', color: '#000' }}>
          <Check size={11} /> {lang === 'it' ? 'Prenota ora' : 'Book now'}
        </div>
      </div>
    </div>
  );
}

/* ─── Full Showcase Section ─── */
export function ShowcaseSection({ lang }) {
  const rows = [
    {
      label: lang === 'it' ? 'Pannello Admin' : 'Admin Panel',
      title: lang === 'it' ? 'Tutto sotto controllo.\nIn un\'unica schermata.' : 'Everything under control.\nIn one screen.',
      description: lang === 'it'
        ? 'KPI in tempo reale, calendario booking, incasso mensile e ospiti attivi — tutto centralizzato. Gestisci più proprietà da un pannello senza cambiare strumento.'
        : 'Real-time KPIs, booking calendar, monthly revenue and active guests — all centralized. Manage multiple properties from one panel.',
      bullets: lang === 'it'
        ? ['Prenotazioni e disponibilità in tempo reale', 'Incasso e occupazione per struttura', 'Log accessi e audit completo', 'Multi-proprietà in un\'unica vista']
        : ['Real-time bookings and availability', 'Revenue and occupancy per property', 'Complete access log and audit', 'Multi-property unified view'],
      mockup: <DashboardMockup lang={lang} />,
      url: 'app.shangrilux.com/panel/villa-aurora',
      imgRight: true,
    },
    {
      label: lang === 'it' ? 'Smart Home' : 'Smart Home',
      title: lang === 'it' ? '18 brand connessi.\nUn\'unica interfaccia.' : '18 connected brands.\nOne interface.',
      description: lang === 'it'
        ? 'Controlla serrature, clima, luci e cancelli di 18 brand — Nuki, KNX, Loxone, BTicino — direttamente dal pannello. Le chiavi virtuali scadono in automatico al checkout.'
        : 'Control locks, climate, lights and gates of 18 brands — directly from the panel. Virtual keys expire automatically at checkout.',
      bullets: lang === 'it'
        ? ['Chiavi virtuali senza app ospite', 'Controllo in tempo reale per camera', 'Automazioni check-in / check-out', 'Log accessi completo e auditabile']
        : ['App-free virtual keys for guests', 'Per-room real-time control', 'Check-in / check-out automations', 'Full auditable access log'],
      mockup: <SmartHomeMockup lang={lang} />,
      url: 'app.shangrilux.com/panel/villa-aurora',
      imgRight: false,
    },
    {
      label: lang === 'it' ? 'Portale Prenotazioni' : 'Booking Portal',
      title: lang === 'it' ? 'Il portale che\ni tuoi ospiti ameranno.' : 'The portal your\nguests will love.',
      description: lang === 'it'
        ? 'Calendario disponibilità, tariffe dinamiche per data e conferma automatica via email — tutto white-label, senza commissioni a terze parti.'
        : 'Availability calendar, dynamic pricing per date, and automatic email confirmation — all white-label, no third-party commissions.',
      bullets: lang === 'it'
        ? ['Calendario con tariffe visibili per data', 'Selezione servizi aggiuntivi', 'Conferma e PDF automatici', 'Zero commissioni a terze parti']
        : ['Calendar with per-date pricing', 'Add-on service selection', 'Auto confirmation and PDF', 'Zero third-party commissions'],
      mockup: <BookingPortalMockup lang={lang} />,
      url: 'app.shangrilux.com/booking/villa-aurora',
      imgRight: true,
    },
  ];

  return (
    <section className="py-14 md:py-24 px-5 sm:px-6 overflow-hidden"
      style={{ background: 'var(--lux-surface-1)', borderTop: '1px solid var(--lux-border-1)', borderBottom: '1px solid var(--lux-border-1)' }}>
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-10 md:mb-20">
          <p className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: 'var(--lux-gold)' }}>
            {lang === 'it' ? 'Interfaccia reale' : 'Real interface'}
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light mb-4" style={{ color: 'var(--lux-text)' }}>
            {lang === 'it'
              ? <>{lang === 'it' ? 'La piattaforma' : 'The platform'}<br /><span style={{ color: 'var(--lux-gold)' }}>{lang === 'it' ? 'in azione' : 'in action'}</span></>
              : <>The platform<br /><span style={{ color: 'var(--lux-gold)' }}>in action</span></>}
          </h2>
          <p className="text-sm max-w-2xl mx-auto px-2" style={{ color: 'var(--lux-text-muted)' }}>
            {lang === 'it'
              ? 'White-label al 100%. Il tuo nome, la tua struttura — ogni schermata personalizzata.'
              : '100% white-label. Your name, your property — every screen personalized.'}
          </p>
        </div>

        {/* Alternating rows */}
        {rows.map((row, idx) => {
          const textBlock = (
            <div className="w-full lg:w-[40%] shrink-0">
              <p className="text-xs uppercase tracking-[0.3em] mb-3 sm:mb-4" style={{ color: 'var(--lux-gold)' }}>{row.label}</p>
              <h3 className="font-serif text-2xl sm:text-3xl font-light mb-3 sm:mb-4 leading-tight" style={{ color: 'var(--lux-text)' }}>
                {row.title.split('\n').map((l, i) => <span key={i}>{l}{i < row.title.split('\n').length - 1 && <br />}</span>)}
              </h3>
              <p className="text-sm leading-relaxed mb-5 sm:mb-6" style={{ color: 'var(--lux-text-muted)' }}>{row.description}</p>
              <ul className="space-y-2 sm:space-y-2.5">
                {row.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm" style={{ color: 'var(--lux-text-sec)' }}>
                    <div className="w-4 h-4 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                      style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)' }}>
                      <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--lux-gold)' }} />
                    </div>
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          );

          const mockupBlock = (
            <div className="w-full lg:flex-1 relative">
              {/* Browser chrome */}
              <div className="rounded-sm overflow-hidden shadow-2xl relative max-h-[380px] sm:max-h-[500px] lg:max-h-none"
                style={{ border: '1px solid rgba(201,168,76,0.2)' }}>
                <div className="flex items-center gap-2 px-3 sm:px-4 py-2"
                  style={{ background: '#0c0c0c', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <div className="flex gap-1 sm:gap-1.5">
                    {['#3a3a3a', '#444', '#444'].map((c, i) => (
                      <div key={i} className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full" style={{ background: c }} />
                    ))}
                  </div>
                  <div className="flex-1 mx-2 sm:mx-4 px-2 sm:px-3 py-1 rounded text-[9px] sm:text-[10px] text-center truncate"
                    style={{ background: '#1a1a1a', color: '#3a3a3a', maxWidth: 300, margin: '0 auto', fontFamily: 'monospace' }}>
                    {row.url}
                  </div>
                  <div className="w-8 sm:w-16 shrink-0" />
                </div>
                <div style={{ background: '#0a0a0a', overflow: 'hidden' }}>
                  {row.mockup}
                </div>
              </div>
            </div>
          );

          return (
            <div key={idx}
              className={`flex flex-col ${row.imgRight ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8 lg:gap-16 mb-12 lg:mb-28 last:mb-0`}>
              {textBlock}
              {mockupBlock}
            </div>
          );
        })}
      </div>
    </section>
  );
}
